import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { keyframes, css } from 'styled-components/macro';
import Clipboard from 'react-clipboard.js';
import ThemeContext from '../utils/theming';

import SocialShareGroup from './SocialShareFooter/SocialShareGroup';
import BackButton from './Buttons/BackButton';
import { detect } from '../utils/detect-browser';

import { triggerFindYourRep } from '../containers/LocalRep/actions';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../utils/nextActionUtil';

import {
    goToPrevAction,
    setOverlayPartnerVisible,
    closeShareDrawer,
    copyToClipboard,
    openShareDrawer,
    setOverlayVisible
} from '../containers/Widget/actions';

import { ActionTypes, CardTypes } from '../utils/enums';
import { width, widthEm, isPortrait as isPortrait_ } from '../utils/size';
import staticData from '../static_data/all';
import { copyFor } from '../copy/copy';

import getIsLegacyDonation from '../utils/donation/isLegacyDonation';


const revealShareDrawerContent = y => keyframes`
    0% {
      transform: translateY(0)
    }

    100% {
      transform: translateY(${y})
    }
`;

const isSafari = detect().name === 'safari';

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    position: relative;
    justify-content: space-between;
    align-items: ${({ hasMiddleText }) =>
        width() < 450 && hasMiddleText ? 'start' : 'center'};
    padding: 0 0.5em;
    ${() => width() < 310 && `padding: 0`};
    z-index: ${props => (props.shareDrawerVisibility ? '3' : '1')};
    background: ${props =>
        props.backgroundColor
            ? props.backgroundColor
            : props.hasNextAction
            ? '#f8f8f7'
            : '#fff'};
`;

const Container = styled.div`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    align-items: ${isSafari ? 'baseline' : 'flex-end'};
    font-family: Lato;
    height: 16px;
    padding-top: ${({ hasMiddleText }) => (width() < 450 && hasMiddleText ? '7px' : '0')};
    line-height: ${({ hasMiddleText }) => (width() < 450 && hasMiddleText ? '1.4' : '1')};

    .poweredBy {
        margin: 0 0 0 auto;
        width: ${({ hasMiddleText }) =>
            width() < 450 && hasMiddleText ? '100%' : 'initial'};
        text-align: end;
        margin-right: ${({ small }) => (small ? 10 : 16)}px;
    }

    > span,
    .poweredBy {
        font-size: 10px;
        color: #008eff;
        cursor: pointer;

        &:hover {
            text-shadow: 0 0 0.01px #008eff;
        }

        a,
        span,
        .poweredBy {
            text-decoration: none;
            color: #008eff;
            cursor: pointer;
            padding: 0;
            &:hover {
                text-shadow: 0 0 0.01px #008eff;
            }
        }
        &:not(:last-child) {
            margin-right: ${({ small }) => (small ? 10 : 16)}px;
        }
    }
`;

const ShareDrawerContent = styled.div`
    position: absolute;
    left: -1em;
    width: ${({ isVisual, isPortrait }) =>
        isVisual && !isPortrait ? `calc(150% + 1em + 1px);` : `calc(100% + 2em);`};
    padding: 16px 18px;
    background: #fff;
    z-index: 3;
    ${props => `transform: translateY(${props.drawerHeight})`};
    animation: ${props => css`
        ${revealShareDrawerContent(props.drawerHeight)} 0.3s linear alternate
    `};
`;

const ShareSection = styled.div`
    width: 174px;
    float: left;

    @media (max-width: 30em) {
        width: 100%;
    }
`;

const EmbedSection = styled.div`
    width: calc(100% - 204px);
    float: left;
    margin-left: 30px;
    position: relative;

    @media (max-width: 30em) {
        width: 100%;
        margin-left: 0;
        margin-top: 12px;
    }
`;

const ShareBackground = styled.div`
    background: #000;
    position: fixed;
    left: 5px;
    top: 2px;
    width: calc(100% - 10px);
    height: calc(100% - 4px);
    transition: opacity 0.3s;
    opacity: 0;
    visibility: hidden;

    @media (max-width: 30em) {
        left: 0;
        width: 100%;
    }

    ${props =>
        props.shareDrawerVisibility &&
        ` opacity: 0.5;
          visibility: visible;
        `};
`;

const Title = styled.h2`
    font-family: Averta;
    font-size: 16px;
    font-weight: normal;
    line-height: 1.63;
    color: #000000;
    margin: 0;
`;

const SocialShareContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    align-items: flex-start;
    margin-top: 8px;
`;

const Input = styled.input`
    border-radius: 2px;
    border: solid 1px #f1f1ee;
    width: 100%;
    height: 36px;
    line-height: 36px;
    font-family: Averta;
    font-size: 12px;
    margin-top: 8px;
    padding: 0 10px;
    appearance: none;

    @media (max-width: 30em) {
        height: 52px;
        line-height: 52px;
        font-size: 14px;
    }

    &:focus {
        outline: none;
        border: 1px solid ${({ theme }) => theme.colors.primary};
    }
`;

const Button = styled.div`
    position: absolute;
    right: -1px;
    bottom: 1px;
    height: 34px;
    line-height: 32px;
    padding: 0 10px;

    @media (max-width: 30em) {
        height: 50px;
        line-height: 50px;
    }

    &:before {
        content: '';
        width: 100px;
        height: 100%;
        float: left;
        background-image: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
    }

    button {
        border: 0;
        color: ${({ theme }) => theme.colors.primary};
        font-size: 14px;
        display: inline-block;
        background: #fff;
        cursor: pointer;
        font-weight: 600;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

        &:hover,
        &:focus {
            outline: none;
        }
    }
`;

const CloseButton = styled.div`
    width: 14px;
    height: 26px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 1.08;
    text-align: left;
    color: #5d656c;
    margin-top: -10px;
    cursor: pointer;
    position: absolute;
    right: 18px;
    top: 28px;
`;

const NoShareBottomDrawer = props => (
    <Wrapper
        isPortrait={props.isPortrait}
        shareDrawerVisibility={props.shareDrawerVisibility}
        backgroundColor={props.backgroundColor}
        hasNextAction={props.nextAction !== null}
        hasMiddleText={false}
    >
        <Container small={width() < 340} hasMiddleText={false}>
            {props.children}
            {props.localRepFound && props.useLocalRepresentative && (
                <span>
                    {copyFor('findReps', 'notYour')}&nbsp;&nbsp;
                    <span onClick={props.triggerFindYourRep}>
                        {props.isPortrait
                            ? copyFor('findReps', 'clickToFindShort')
                            : copyFor('findReps', 'clickToFind')}
                    </span>
                </span>
            )}
            {props.isEndCard && !props.isEmailSubmitted && (
                <span>
                    {!props.isPortrait && copyFor('footer', 'stayInformed')}&nbsp;
                    {!props.isPortrait && props.width < 350 && <br />}
                    <span onClick={() => props.setOverlayVisible(true)}>
                        {copyFor('footer', 'newsletterSignup')}
                    </span>
                </span>
            )}
            <span>
                <a
                    href="https://actionbutton.org/terms"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={proxy => proxy.stopPropagation()}
                >
                    {copyFor('footer', 'terms')}
                </a>
            </span>
            {props.showPoweredBy && (
                <div className="poweredBy" onClick={() => props.setOverlayVisible(true)}>
                    {copyFor('footer', 'poweredBy')}
                </div>
            )}
        </Container>
    </Wrapper>
);

const ShareBottomDrawer = props => {
    const showRepStance =
        !props.newsletterSignUpVisible &&
        props.useLocalRepresentative &&
        props.isRepStance;
    const showFindReps =
        !props.newsletterSignUpVisible &&
        props.localRepFound &&
        props.useLocalRepresentative &&
        !props.isRepStance;
    const showBackButton = props.hasBackButton && props.goBack;
    const showNewsletterSignup = !props.isEmailSubmitted;

    const hasMiddleText =
        (props.isVolunteeringAction && !props.isVolunteerFrontCard) || showRepStance;
    const theme = useContext(ThemeContext);

    return (
        <Wrapper
            isPortrait={props.isPortrait}
            shareDrawerVisibility={props.shareDrawerVisibility}
            backgroundColor={props.backgroundColor}
            hasNextAction={props.nextAction !== null}
            hasMiddleText={hasMiddleText}
        >
            <ShareBackground
                shareDrawerVisibility={props.shareDrawerVisibility}
                onClick={() => props.closeShareDrawer()}
            />
            {props.shareDrawerVisibility && (
                <ShareDrawerContent
                    className="ie-share-drawer-content"
                    isPortrait={props.isPortrait}
                    isVisual={props.isVisual}
                    drawerHeight={widthEm() > 30 ? `-30px` : `-88px`}
                >
                    <ShareSection>
                        <Title>{copyFor('footer', 'share')}</Title>
                        <SocialShareContainer>
                            <SocialShareGroup
                                isEmbed={props.isEmbed}
                                shareUrl={props.shareUrl}
                                widgetRequestId={props.widgetRequestId}
                                twitterMention={props.twitterMention}
                                publisherName={props.publisherName}
                                partnerName={props.partnerName}
                                actionType={props.actionType}
                                actionShared={props.actionShared}
                            />
                        </SocialShareContainer>
                    </ShareSection>
                    <EmbedSection>
                        <Title>{copyFor('footer', 'embed')}</Title>
                        <Input
                            id="embedIframe"
                            theme={theme}
                            defaultValue={props.iframeSnippet}
                            readOnly
                        />
                        <Button theme={theme}>
                            <Clipboard
                                data-clipboard-text={props.iframeSnippet}
                                onClick={() => props.copyToClipboard()}
                            >
                                {props.copiedToClipboard
                                    ? copyFor('footer', 'copied')
                                    : copyFor('footer', 'copy')}
                            </Clipboard>
                        </Button>
                    </EmbedSection>
                    <CloseButton onClick={() => props.closeShareDrawer()}>+</CloseButton>
                </ShareDrawerContent>
            )}
            <Container small={width() < 340} hasMiddleText={hasMiddleText}>
                {!props.isEndCard && !showBackButton && props.canGoBack && (
                    <BackButton
                        onClick={e => {
                            e.stopPropagation();
                            if (props.beforeBack) props.beforeBack();
                            if (!props.dontGoToPrevAction) props.goToPrevAction(props.previousAction);
                        }}
                    />
                )}
                {showBackButton && (
                    <BackButton
                        onClick={e => {
                            e.stopPropagation();
                            if (props.beforeBack) props.beforeBack();
                            props.goBack();
                        }}
                    />
                )}
                {props.children}
                {(props.isRepStance || (showFindReps && props.isContactAction)) && (
                    <span onClick={props.triggerFindYourRep}>
                        {props.multipleReps
                            ? copyFor('findReps', 'notYourPlural')
                            : copyFor('findReps', 'notYour')}
                    </span>
                )}
                <span onClick={() => props.openShareDrawer()}>
                    {copyFor('footer', 'share')}
                </span>
                <span>
                    <a
                        href="https://actionbutton.org/terms"
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={proxy => proxy.stopPropagation()}
                    >
                        {copyFor('footer', 'terms')}
                    </a>
                </span>
                {showNewsletterSignup &&
                    ((props.isVolunteeringAction && !props.isPortrait) ||
                        (showRepStance && !props.isPortrait)) && (
                        <span onClick={() => props.setOverlayVisible(true)}>
                            {copyFor('footer', 'newsletterSignup')}
                        </span>
                    )}
                {props.showParticipantMediaLinks && (
                    <span style={{ color: '#000', cursor: 'default' }}>
                        {`Participant’s `}
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://participant.com/terms-use"
                        >
                            Terms
                        </a>
                        {` and `}
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://participant.com/privacy-policy"
                        >
                            Privacy
                        </a>
                    </span>
                )}
                {props.isVolunteeringAction && !props.isVolunteerFrontCard && (
                    <span
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flex: 1
                        }}
                    >
                        <span
                            style={{
                                color: '#000000',
                                cursor: 'default',
                                textShadow: 'none'
                            }}
                        >
                            {width() < 340 ? 'Data by' : 'Powered By '}
                        </span>
                        &nbsp;
                        <span
                            id="votesmart"
                            onClick={event =>
                                props.setOverlayPartnerVisible(true, event, true)
                            }
                        >
                            {'Volunteer Match'}
                        </span>
                    </span>
                )}
                {showRepStance && (
                    <span
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flex: 1
                        }}
                    >
                        <span
                            style={{
                                color: '#000000',
                                cursor: 'default',
                                textShadow: 'none'
                            }}
                        >
                            {props.isPortrait
                                ? copyFor('footer', 'sourceShort', 'repStatus')
                                : copyFor('footer', 'source', 'repStatus')}
                        </span>
                        &nbsp;
                        <span
                            id="votesmart"
                            onClick={event =>
                                props.setOverlayPartnerVisible(
                                    true,
                                    event,
                                    false,
                                    false,
                                    true
                                )
                            }
                        >
                            {'Vote Smart'}
                        </span>
                    </span>
                )}
                {props.showPoweredBy && (
                    <div
                        className="poweredBy"
                        onClick={() => props.setOverlayVisible(true)}
                    >
                        {copyFor('footer', 'poweredBy')}
                    </div>
                )}
            </Container>
        </Wrapper>
    );
};

const sharedProptypes = {
    actionShared: PropTypes.func,
    actionType: PropTypes.string,
    activeAction: PropTypes.object,
    backgroundColor: PropTypes.string,
    beforeBack: PropTypes.func,
    children: PropTypes.object,
    closeShareDrawer: PropTypes.func,
    copiedToClipboard: PropTypes.bool,
    copyToClipboard: PropTypes.func,
    displayServiceFooter: PropTypes.bool.isRequired,
    goToPrevAction: PropTypes.func,
    iframeSnippet: PropTypes.string,
    isActionChoiceCard: PropTypes.bool.isRequired,
    isContactAction: PropTypes.bool,
    isEmailSubmitted: PropTypes.bool,
    isEmbed: PropTypes.bool.isRequired,
    isPortrait: PropTypes.bool,
    isRepStance: PropTypes.bool.isRequired,
    isVisual: PropTypes.bool,
    isVolunteerFrontCard: PropTypes.bool.isRequired,
    isVolunteeringAction: PropTypes.bool.isRequired,
    localRepFound: PropTypes.bool,
    multipleReps: PropTypes.bool,
    newsletterSignUpVisible: PropTypes.bool,
    nextAction: PropTypes.object,
    triggerFindYourRep: PropTypes.func.isRequired,
    openShareDrawer: PropTypes.func,
    partnerName: PropTypes.string,
    prevActionIsPoll: PropTypes.bool,
    publisherName: PropTypes.string,
    setOverlayVisible: PropTypes.func,
    shareDrawerVisibility: PropTypes.bool,
    showParticipantMediaLinks: PropTypes.bool,
    showPoweredBy: PropTypes.bool,
    twitterMention: PropTypes.string,
    useLocalRepresentative: PropTypes.bool.isRequired,
    widgetRequestId: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired
};

NoShareBottomDrawer.propTypes = { ...sharedProptypes };
ShareBottomDrawer.propTypes = { ...sharedProptypes };

const ShareDrawer = staticData.useStatic ? NoShareBottomDrawer : ShareBottomDrawer;

export default connect(
    (state, props) => {
        const { activeAction } = state.widget;
        const nextAction = getNextAction(
            state.widget.actions,
            activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const currentCard = state.widget.cards[state.widget.activeAction.order - 1];
        const previousAction =
            state.widget.actions.find(
                a => a.id === currentCard.order.dependentActionId
            ) ||
            state.widget.actions.find(
                a => a.order === state.widget.activeAction.order - 1
            );
        const prevActionIsPoll = (previousAction || {}).type === 'poll';
        const currentOrderHasCondionalSequencing = !!state.widget.cards.find(c => {
            return c.order.value === currentCard.order.value && c.order.type === 2;
        });
        const canGoBack = props.canGoBack || (prevActionIsPoll && currentOrderHasCondionalSequencing);

        const isLegacyDonation = getIsLegacyDonation(state);

        return {
            shareUrl: state.widget.articleUrl,
            newsletterSignUpVisible:
                props.newsletterSignUpVisible && state.widget.showNewsletterSignup,
            copiedToClipboard: state.widget.textCopiedToClipboard,
            iframeSnippet: `<iframe style="width: 100%" frameborder="0" scrolling="no" height="450" src="https://embed.actionbutton.co/widget/widget-iframe.html?widgetId=${state.widget.widgetRequestId}"></iframe>`,
            isActionChoiceCard: currentCard.type === CardTypes.actionChoice,
            displayServiceFooter: state.widget.publisherId === 54, // MSN ID
            isRepStance: activeAction.type === ActionTypes.CheckRepPosition,
            isVolunteerFrontCard: state.router.location.pathname === '/preload/volunteer',
            isContactAction:
                activeAction.type === ActionTypes.Email ||
                activeAction.type === ActionTypes.Tweet ||
                activeAction.type === ActionTypes.Call ||
                activeAction.type === ActionTypes.Petition ||
                (activeAction.type === ActionTypes.RecordVideo &&
                    state.router.location.pathname !== '/recordvideo'), //hide 'not your rep' for instructions
            isVolunteeringAction: activeAction.type === ActionTypes.Volunteer,
            isEmailSubmitted: state.widget.isEmailSubmitted,
            isEmbed: state.widget.articleUrl.includes('embed'),
            localRepFound: state.localRep.localRepData.representative !== undefined,
            nextAction,
            activeAction,
            isVisual: activeAction.isVisual,
            shareDrawerVisibility: state.widget.shareDrawerVisibility,
            showPoweredBy:
                state.widget.isSponsored ||
                state.widget.createdByThirdParty ||
                isLegacyDonation,
            canGoBack,
            previousAction,
            showParticipantMediaLinks: (activeAction.partner || {}).id === 331 || false,
            useLocalRepresentative:
                !!activeAction.representativeSearch ||
                activeAction.type === ActionTypes.CheckRepPosition,
            widgetRequestId: state.widget.widgetRequestId,
            width: width(),
            isPortrait: isPortrait_()
        };
    },
    {
        closeShareDrawer,
        copyToClipboard,
        goToPrevAction,
        triggerFindYourRep,
        openShareDrawer,
        setOverlayPartnerVisible,
        setOverlayVisible
    }
)(ShareDrawer);
